import { EnvironmentProviders, provideAppInitializer, Provider } from '@angular/core';

import { IonicPermanentStorageService } from '@guardfinder/permanent-storage';

import { Consent } from '../../Domain/Model/Consent/consent';
import { AnalyticsService } from '../../Domain/Port/analytics.service';
import { ConsentRepository } from '../../Domain/Port/consent.repository';
import { EventRepository } from '../../Domain/Port/event-repository';
import { FirebaseErrorLogger } from '../../Domain/Port/firebase-error.logger';
import { FirebaseConsoleErrorLogger } from '../../Infrastructure/Adapter/Firebase/firebase-console-error.logger';
import { PermanentStorageConsentRepository } from '../../Infrastructure/Adapter/LocalStorage/permanent-storage-consent-repository.service';
import { ConsentAwareEventRepositoryServiceDecorator } from '../Service/consent-aware-analytics-service-decorator.service';
import { setupAnalyticsRouterTracking } from './analytics-router-tracking';
import { eventRepositoryFactory } from './factory/event-repository.factory';
import { originAnalyticsServiceFactory } from './factory/firebase-analytics-origin.factory';
import { firebaseAnalyticsConsentStorage, firebaseAnalyticsStorageKey } from './injection-token/consent-storage.it';
import { FIREBASE_CONFIG_IT } from './injection-token/firebase-config.it';
import { FIREBASE_IS_NATIVE_PLATFORM_IT } from './injection-token/firebase-is-native-platform.it';
import { FIREBASE_ANALYTICS_IT } from './injection-token/firebase-origin-service.it';

/**
 * Provides a configured array of dependency injection providers and environment providers
 * necessary to set up Firebase Analytics services in the application.
 *
 * The function returns an array of objects defining the dependencies and their respective
 * implementations or factories. These configurations are used to initialize Firebase Analytics-related
 * services, repositories, and loggers, ensuring proper event tracking and error logging functionalities.
 *
 * Specific configurations include:
 * - Dependency injection for consent repository, analytics service, and event repository.
 * - Factory-based initialization for platform-specific analytics services, error logging, and persistent storage.
 * - Registration of application initializers for router event tracking.
 *
 * @returns {Array<Provider | EnvironmentProviders>} An array containing provider definitions and environment providers.
 */
export const provideFirebaseAnalyticsService = (): Array<Provider | EnvironmentProviders> => {
    return [
        {
            provide: ConsentRepository,
            useClass: PermanentStorageConsentRepository
        },
        {
            provide: AnalyticsService,
            useClass: ConsentAwareEventRepositoryServiceDecorator
        },
        {
            provide: EventRepository,
            useFactory: eventRepositoryFactory,
            deps: [FIREBASE_IS_NATIVE_PLATFORM_IT, FirebaseErrorLogger, FIREBASE_ANALYTICS_IT]
        },
        {
            provide: FIREBASE_ANALYTICS_IT,
            useFactory: originAnalyticsServiceFactory,
            deps: [FIREBASE_CONFIG_IT]
        },
        {
            provide: FirebaseErrorLogger,
            useClass: FirebaseConsoleErrorLogger
        },
        {
            provide: firebaseAnalyticsConsentStorage,
            useFactory: () => new IonicPermanentStorageService<Consent>(firebaseAnalyticsStorageKey)
        },
        provideAppInitializer(setupAnalyticsRouterTracking()) // Router events registration to track the screen change
    ];
};
